import React from "react";
import { GoogleDetails } from "../../../types";

export type GoogleReviewsServerProps = {
    buttonTitle?: string;
    googleDetails: GoogleDetails;
    hideStars?: boolean;
    showQRCode?: boolean;
    qrCodeImageUrl?: string;
    title?: string;
    description?: string;
    ratingText?: string;
    reviewCountText?: string;
    showGoogleLogo?: boolean;
    googleLogoSize?: number;
    image?: {
        src: string;
        alt?: string;
        width?: number;
        height?: number;
    };
};

const Star = () => (
    <svg viewBox="0 0 24 24" width="38" height="38" fill="#ffb300">
        <path d="M12 2.5l2.93 5.94 6.56.95-4.75 4.63 1.12 6.53L12 17.77 6.14 20.55l1.12-6.53L2.5 9.39l6.56-.95L12 2.5z" />
    </svg>
);

const GoogleLogo = ({ size = 40 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.73 1.22 9.24 3.61l6.91-6.91C35.91 2.44 30.37 0 24 0 14.82 0 6.73 5.15 2.69 12.68l8.06 6.26C12.7 13.16 17.9 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.14 24.5c0-1.64-.15-3.21-.43-4.72H24v9h12.44c-.54 2.91-2.18 5.38-4.64 7.05l7.19 5.59C43.98 36.91 46.14 31.23 46.14 24.5z"/>
        <path fill="#FBBC05" d="M10.75 28.94A14.5 14.5 0 019.5 24c0-1.72.3-3.37.84-4.94l-8.06-6.26A23.93 23.93 0 000 24c0 3.87.93 7.53 2.58 10.74l8.17-5.8z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.92-2.13 15.9-5.79l-7.19-5.59c-2 1.35-4.56 2.15-8.71 2.15-6.1 0-11.3-3.66-13.25-8.84l-8.17 5.8C6.73 42.85 14.82 48 24 48z"/>
    </svg>
);

const GoogleReviewsServer = ({
                                 buttonTitle,
                                 googleDetails,
                                 hideStars = false,
                                 showQRCode = true,
                                 qrCodeImageUrl,
                                 title,
                                 description,
                                 ratingText,
                                 reviewCountText,
                                 showGoogleLogo = false,
                                 googleLogoSize = 40,
                                 image,
                             }: GoogleReviewsServerProps) => {
    const { googleReviewLink } = googleDetails;

    if (!googleReviewLink) return null;

    return (
        <div style={{ padding: "8px 0" }}>
            {/* Scoped styles */}
            <style>
                {`
                .grs-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .grs-description {
                    max-width: 42rem;
                }

                .grs-button {
                    background-color: #2563eb;
                    color: #fff;
                    padding: 10px 16px;
                    border-radius: 6px;
                    text-decoration: none;
                    display: inline-block;
                    transition: background-color 0.2s ease;
                    width: 80%;
                    text-align: center;
                }

                .grs-button:hover {
                    background-color: #1d4ed8;
                }

                @media (min-width: 640px) {
                    .grs-button {
                        width: auto;
                    }
                }
                `}
            </style>

            <div className="grs-container">
                {showGoogleLogo && (
                    <div style={{ marginBottom: "12px" }}>
                        <GoogleLogo size={googleLogoSize} />
                    </div>
                )}

                {!!title && (
                    <h3 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "12px", textAlign: "center" }}>
                        {title}
                    </h3>
                )}

                {!!description && (
                    <p
                        className="grs-description"
                        style={{
                            textAlign: "center",
                            color: "#525252",
                            marginBottom: "20px",
                            lineHeight: 1.6,
                        }}
                    >
                        {description}
                    </p>
                )}

                {!!image?.src && (
                    <div style={{ marginBottom: "24px" }}>
                        <img
                            src={image.src}
                            alt={image.alt || ""}
                            width={image.width}
                            height={image.height}
                            style={{
                                maxWidth: "100%",
                                borderRadius: "12px",
                            }}
                        />
                    </div>
                )}

                {!hideStars && (
                    <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} />
                        ))}
                    </div>
                )}

                {(ratingText || reviewCountText) && (
                    <div style={{ textAlign: "center", marginBottom: "20px" }}>
                        {ratingText && (
                            <p style={{ fontWeight: 600 }}>{ratingText}</p>
                        )}
                        {reviewCountText && (
                            <p style={{ fontSize: "14px", color: "#737373" }}>
                                {reviewCountText}
                            </p>
                        )}
                    </div>
                )}

                {showQRCode && qrCodeImageUrl && (
                    <div style={{ marginBottom: "20px" }}>
                        <img
                            src={qrCodeImageUrl}
                            alt="QR code"
                            width={200}
                            height={200}
                        />
                    </div>
                )}

                {!!buttonTitle && (
                    <a
                        href={googleReviewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="grs-button"
                    >
                        {buttonTitle}
                    </a>
                )}
            </div>
        </div>
    );
};

export default GoogleReviewsServer;